import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import CustomerForm from './CustomerForm';
import { API_URL } from './apiurl';

function CustomersList() {
  const { auth } = useContext(AuthContext);

  const [customers, setCustomers] = useState([]);
  const [editingCustomer, setEditingCustomer] = useState(null);

  // Info-Overlay-States
  const [infoCustomer, setInfoCustomer] = useState(null);
  const [selectedCustomerHours, setSelectedCustomerHours] = useState(null);

  const [isFormOpen, setIsFormOpen] = useState(false);

  useEffect(() => {
    fetchCustomers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1. Kunden laden
  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/customers`, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Laden der Kunden.');
      }

      const data = await response.json();
      setCustomers(data);
    } catch (err) {
      console.error('Fehler beim Laden der Kunden:', err);
      toast.error(err.message || 'Serverfehler beim Laden der Kunden.');
    }
  };

  // 2. Kundenstunden abrufen und Info anzeigen
  const fetchCustomerHours = async (customerId) => {
    try {
      const response = await fetch(`${API_URL}/customers/${customerId}/hours`, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Abrufen der Kundenstunden.');
      }

      const data = await response.json();

      // Kundenstunden speichern
      setSelectedCustomerHours(data.totalHours);

      // Info-Overlay öffnen
      const found = customers.find((customer) => customer.id === customerId);
      setInfoCustomer(found || null);
    } catch (err) {
      console.error('Fehler beim Abrufen der Kundenstunden:', err);
      toast.error(err.message || 'Serverfehler beim Abrufen der Kundenstunden.');
    }
  };

  // 3. Bearbeiten
  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setIsFormOpen(true);
  };

  // 4. Formular schließen
  const handleFormClose = () => {
    setEditingCustomer(null);
    setIsFormOpen(false);
    fetchCustomers(); // Liste aktualisieren
  };

  // 5. Löschen
  const handleDelete = async (customer) => {
    if (!window.confirm(`Möchten Sie ${customer.vorname} ${customer.nachname} wirklich löschen?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/customers/${customer.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Löschen des Kunden.');
      }

      setCustomers(customers.filter((c) => c.id !== customer.id));
      toast.success('Kunde erfolgreich gelöscht.');
    } catch (err) {
      console.error('Fehler beim Löschen des Kunden:', err);
      toast.error(err.message || 'Serverfehler beim Löschen des Kunden.');
    }
  };

  // 6. CSV-Export-Header definieren
  const headers = () => [
    "date",
    "startTime",
    "endTime",
    "hours",
    "subject",
    "type",
    "address",
    "hotelspeesen",
    "verpflegungsMehraufwand",
    "customerId"
  ];

  // 7. CSV-Export ausführen
  const handleExport = async (customer) => {
    toast.info(`CSV-Export für ${customer.vorname} ${customer.nachname} gestartet.`);

    let datenx = [];
    try {
      const response = await fetch(`${API_URL}/customers/${customer.id}/csv`, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Abrufen der CSV-Daten.');
      }

      datenx = await response.json();
    } catch (err) {
      toast.error(err.message || 'Serverfehler beim Abrufen der CSV-Daten.');
      return;
    }

    const csvRows = [];
    const csvHeaders = headers().join(';');
    csvRows.push(csvHeaders);

    let kundenNr = "";
    datenx.forEach(entry => {
      const row = headers().map(header => {
        const value = entry[header];
        if (header === "customerId") {
          kundenNr = value;
        }
        if (typeof value === 'string') {
          // Entfernen von Anführungszeichen und ersetzen von Semikolons
          return value.replace(/"/g, '').replace(/;/g, ',');
        }
        return value;
      }).join(';');
      csvRows.push(row);
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `stundenbuchungen_kunde_${kundenNr}_${new Date().toLocaleDateString().replace(/\./g, '-')}.csv`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Kundenübersicht</h2>

      <button onClick={() => setIsFormOpen(true)} style={styles.addButton}>
        Neuen Kunden hinzufügen
      </button>

      {/* Formular (Modal) für Kunden anlegen oder bearbeiten */}
      {isFormOpen && (
        <CustomerForm onClose={handleFormClose} editingCustomer={editingCustomer} />
      )}

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Vorname</th>
              <th style={styles.th}>Nachname</th>
              <th style={styles.th}>Straße</th>
              <th style={styles.th}>PLZ</th>
              <th style={styles.th}>Ort</th>
              <th style={styles.th}>Telefon</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((customer) => (
              <tr key={customer.id} style={styles.row}>
                <td style={styles.td}>{customer.vorname}</td>
                <td style={styles.td}>{customer.nachname}</td>
                <td style={styles.td}>{customer.strasse}</td>
                <td style={styles.td}>{customer.plz}</td>
                <td style={styles.td}>{customer.ort}</td>
                <td style={styles.td}>{customer.telefon}</td>
                <td style={styles.td}>{customer.email}</td>
                <td style={styles.td}>
                  <button
                    onClick={() => handleEdit(customer)}
                    style={styles.iconButton}
                    title="Bearbeiten"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDelete(customer)}
                    style={styles.iconButton}
                    title="Löschen"
                  >
                    🗑️
                  </button>
                  <button
                    onClick={() => fetchCustomerHours(customer.id)}
                    style={styles.iconButton}
                    title="Info anzeigen"
                  >
                    ℹ️
                  </button>
                  <button
                    onClick={() => handleExport(customer)}
                    style={styles.iconButton}
                    title="CSV exportieren"
                  >
                    📄
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Info-Overlay statt "Seitenende" */}
      {infoCustomer && selectedCustomerHours !== null && (
        <div style={styles.infoOverlay}>
          <h3 style={styles.infoTitle}>
            Informationen zu {infoCustomer.vorname} {infoCustomer.nachname}
          </h3>
          <p>Gesamtstunden: {selectedCustomerHours}</p>
          <button onClick={() => setInfoCustomer(null)} style={styles.closeOverlayButton}>
            Schließen
          </button>
        </div>
      )}
    </div>
  );
}

// -- Styles --
const styles = {
  container: {
    position: 'relative',
    padding: '1.5rem',
    backgroundColor: '#f9fafb',
  },
  title: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    color: '#374151',
  },
  addButton: {
    backgroundColor: '#10b981',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
  tableContainer: {
    overflowX: 'auto',
    marginBottom: '1rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    backgroundColor: '#e5e7eb',
    padding: '0.75rem',
    textAlign: 'left',
    borderBottom: '2px solid #d1d5db',
  },
  td: {
    padding: '0.75rem',
    borderBottom: '1px solid #d1d5db',
  },
  row: {
    cursor: 'default',
  },
  iconButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '1.2rem',
    cursor: 'pointer',
    marginRight: '0.5rem',
  },
  // Das Overlay für die Kundeninformationen
  infoOverlay: {
    position: 'fixed',
    top: '60px', // etwas Abstand vom Seitenrand
    right: '20px',
    width: '300px',
    backgroundColor: '#f1f5f9',
    borderRadius: '8px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
    padding: '1rem',
    zIndex: 1000,
  },
  infoTitle: {
    fontSize: '1.1rem',
    marginBottom: '0.5rem',
    color: '#111827',
  },
  closeOverlayButton: {
    backgroundColor: '#d1d5db',
    color: '#000',
    border: 'none',
    padding: '0.4rem 0.8rem',
    borderRadius: '4px',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
};

export default CustomersList;
